/**
 * Who owns text kept in history state (lib/history-state.ts).
 *
 * Signing out is a hard page load that keeps the browser tab's session
 * history, so the next person who signs in on the same tab can press Back
 * into entries the previous person made. Every history-state value a person
 * typed (search boxes) or chose (the admin "All agents" scope) is stamped
 * with the signed-in user's id, and a stamp that is not the current user's
 * reads as nothing.
 *
 * router.tsx registers the source once (the signed-in user from the auth
 * cache). A leaf module of its own, so the always-loaded bundle does not pull
 * in history-state.ts.
 */

export type HistoryStateOwner = number | string;

type OwnerSource = () => HistoryStateOwner | null | undefined;

let ownerSource: OwnerSource = () => null;

/** Set the owner, or a function that returns the current one (read at every use). */
export function setHistoryStateOwner(owner: HistoryStateOwner | null | OwnerSource): void {
  ownerSource = typeof owner === 'function' ? owner : () => owner;
}

/** The signed-in user's id, or null when no one is signed in (or it is not known yet). */
export function getHistoryStateOwner(): HistoryStateOwner | null {
  let owner: HistoryStateOwner | null | undefined;
  try {
    owner = ownerSource();
  } catch {
    return null;
  }
  if (typeof owner === 'number') return Number.isFinite(owner) ? owner : null;
  if (typeof owner === 'string') return owner.length > 0 && owner.length <= 64 ? owner : null;
  return null;
}
